import { useState } from "react";
import { Box } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ErrorIcon from "@mui/icons-material/Error";
import MenuList from "@mui/material/MenuList";
import format from "date-fns/format";
import I18n from "i18next";

import MzDataGrid from "@markaaz/ui/MzDataGrid";
import MzTooltip from "@markaaz/ui/MzTooltip";
import MzButton from "@markaaz/ui/MzButton";

const sx = {
  root: {},

  delete: {
    cursor: "pointer"
  },

  datagrid: {
    height: "550px",

    ".menu-icon": {
      color: (theme) => theme.palette.blue.c100
    }
  },

  errorStatus: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    svg: {
      color: (theme) => theme.palette.negative.c100,
      fontSize: "16px"
    }
  }
};

const findStatus = (code) => {
  let status;
  switch (code) {
    case "P":
      status = "New";
      break;
    case "E":
      status = "Failed";
      break;
    case "C":
      status = "Processed";
      break;
    case "Q":
      status = "Processing";
      break;
    case "M":
      status = "Processing";
      break;
    default:
      status = code;
  }
  return status;
};

export default function PortfolioMain({ rows, onMenuItemClick, onAddPortfolio, onPageChange }) {
  const t = I18n.getFixedT(null, null, "portfolioBoarding");

  const columns = [
    {
      field: "portfolioName",
      headerName: t("portfolioName"),
      width: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <Box className="MuiDataGrid-cellContent">
          {!row.portfolioName ? (
            <MzButton
              variant="text"
              onClick={() => {
                onAddPortfolio(row);
              }}
            >
              <Typography variant="body2B">Add Portfolio</Typography>
            </MzButton>
          ) : (
            row.portfolioName
          )}
        </Box>
      )
    },
    { field: "fileName", headerName: t("fileName"), width: 150, sortable: false },
    {
      field: "dateUploaded",
      headerName: t("dateUploaded"),
      width: 120,
      sortable: false,
      renderHeader: function () {
        return (
          <Box className="MuiDataGrid-columnHeaderTitle">
            {" "}
            Date <br /> Uploaded{" "}
          </Box>
        );
      },
      valueGetter: function ({ value }) {
        return format(new Date(value), "LL/dd/yy");
      }
    },
    {
      field: "status",
      headerName: t("status"),
      width: 120,
      sortable: false,
      renderCell: ({ row, value }) => {
        return (
          <Box sx={sx.errorStatus} className="MuiDataGrid-cellContent">
            {row.status === "E" && <ErrorIcon />} {value}
          </Box>
        );
      },
      valueGetter: function (data) {
        return findStatus(data.value);
      }
    },
    {
      field: "processingStartDate",
      headerName: t("processingStartDate"),
      width: 110,
      sortable: false,
      align: "center",
      renderHeader: function () {
        return (
          <Box className="MuiDataGrid-columnHeaderTitle">
            {" "}
            Processing <br /> Start Date{" "}
          </Box>
        );
      },
      valueGetter: function ({ value }) {
        if (!value) return "-";
        return format(new Date(value), "LL/dd/yy");
      }
    },
    {
      field: "processingEndDate",
      headerName: t("processingEndDate"),
      width: 110,
      sortable: false,
      align: "center",
      renderHeader: function () {
        return (
          <Box className="MuiDataGrid-columnHeaderTitle">
            {" "}
            Processing <br /> End Date{" "}
          </Box>
        );
      },
      valueGetter: function ({ value }) {
        if (!value) return "-";
        return format(new Date(value), "LL/dd/yy");
      }
    },
    { field: "source", headerName: t("source"), width: 100, sortable: false },
    {
      field: "recordsCount",
      headerName: t("stanumberOfRecordstus"),
      width: 100,
      align: "center",
      sortable: false,
      renderHeader: function () {
        return (
          <Box className="MuiDataGrid-columnHeaderTitle">
            {" "}
            Number Of <br /> Records{" "}
          </Box>
        );
      },
      valueGetter: function ({ value }) {
        if (!value) return "-";
        return value.toLocaleString();
      }
    },
    {
      field: "matchRate",
      headerName: t("matchRate"),
      width: 100,
      align: "center",
      sortable: false,
      renderHeader: function () {
        return (
          <Box className="MuiDataGrid-columnHeaderTitle">
            {" "}
            Match <br /> Rate{" "}
          </Box>
        );
      },
      valueGetter: function ({ value }) {
        if (value === null) return "-";
        return value + "%";
      }
    },
    {
      field: "delete",
      headerName: "",
      width: 30,
      sortable: false,
      renderCell: function (params) {
        return <MenuRow row={params.row} onMenuItemClick={onMenuItemClick} />;
      }
    }
  ];

  return (
    <Box>
      <MzDataGrid
        rowHeight={75}
        sx={sx.datagrid}
        rows={rows?.details}
        paginationMode="server"
        rowCount={rows?.rec_count}
        onPageChange={onPageChange}
        columns={columns}
        disableSelectionOnClick
        disableColumnMenu={true}
        autoPageSize
      />
    </Box>
  );
}

function MenuRow({ row, onMenuItemClick }) {
  const [open, setOpen] = useState(false);

  function toggleMenu(flag) {
    return function () {
      setOpen(flag);
    };
  }

  function handleMenuItemClick(name, row) {
    return function () {
      setOpen(false);
      onMenuItemClick(name, row);
    };
  }

  const Menus = (
    <MenuList>
      <MenuItem disabled={!row.portfolioName} onClick={handleMenuItemClick("remove", row)}>
        <Typography variant="body2">Remove portfolio</Typography>
      </MenuItem>
      <MenuItem onClick={handleMenuItemClick("delete", row)}>
        <Typography variant="body2">Delete file</Typography>
      </MenuItem>
    </MenuList>
  );

  return (
    <ClickAwayListener onClickAway={toggleMenu(false)}>
      <Box sx={sx.delete}>
        <MzTooltip open={open} color="light" click title={Menus}>
          <MoreVertIcon className="menu-icon" onClick={toggleMenu(true)} />
        </MzTooltip>
      </Box>
    </ClickAwayListener>
  );
}
